import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Award, Upload, Search, Plus, Minus, Trash2, X, Edit3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLES = {
  gold: { label: '1st Place - Gold', color: 'text-yellow-500', border: 'border-yellow-500/40', bg: 'bg-yellow-500/10', hoverBg: 'hover:bg-yellow-500/20' },
  silver: { label: '2nd Place - Silver', color: 'text-zinc-300', border: 'border-zinc-400/40', bg: 'bg-zinc-400/10', hoverBg: 'hover:bg-zinc-400/20' },
  bronze: { label: '3rd Place - Bronze', color: 'text-orange-500', border: 'border-orange-500/40', bg: 'bg-orange-500/10', hoverBg: 'hover:bg-orange-500/20' },
  mvp: { label: 'MVP', color: 'text-purple-400', border: 'border-purple-500/40', bg: 'bg-purple-500/10', hoverBg: 'hover:bg-purple-500/20' },
};

export default function AdminBadgesPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploadTier, setUploadTier] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [awardDialog, setAwardDialog] = useState(null); // {userId, userName, tier}
  const [premades, setPremades] = useState([]);
  const [premadeDialog, setPremadeDialog] = useState(null); // tier string
  const [editDesign, setEditDesign] = useState(null); // design object
  const [editName, setEditName] = useState('');
  const [clans, setClans] = useState([]);
  const [clanAwardDialog, setClanAwardDialog] = useState(null); // {clanId, clanName, tier}
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      fetchAll();
    }
  }, [isAdmin, authLoading, navigate]);

  const fetchAll = async () => {
    try {
      const [usersRes, designsRes, premadesRes, clansRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/badge-designs`),
        axios.get(`${API}/badge-designs/premade`),
        axios.get(`${API}/clans`),
      ]);
      setUsers(usersRes.data);
      setDesigns(designsRes.data);
      setPremades(premadesRes.data);
      setClans(clansRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDesign = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadName || !uploadTier) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('tier', uploadTier);
      formData.append('name', uploadName);
      formData.append('file', uploadFile);
      await axios.post(`${API}/admin/badge-designs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Badge design "${uploadName}" created!`);
      setUploadTier(null);
      setUploadName('');
      setUploadFile(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDesign = async (designId, name) => {
    if (!window.confirm(`Delete badge "${name}"? This will also remove it from all players.`)) return;
    try {
      await axios.delete(`${API}/admin/badge-designs/${designId}`);
      toast.success('Badge design deleted');
      fetchAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleUsePremade = async (premadeName, tier) => {
    try {
      await axios.post(`${API}/admin/badge-designs/from-premade`, { premade_name: premadeName, name: premadeName });
      toast.success(`"${premadeName}" added to your ${tier} designs!`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add premade badge');
    }
  };

  const handleAwardBadge = async (userId, designId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/badge`, { design_id: designId, action: 'add' });
      toast.success('Badge awarded!');
      setAwardDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to award badge');
    }
  };

  const handleRemoveBadge = async (userId, designId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/badge`, { design_id: designId, action: 'remove' });
      toast.success('Badge removed');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleEditDesign = async (e) => {
    e.preventDefault();
    if (!editDesign || !editName) return;
    try {
      await axios.put(`${API}/admin/badge-designs/${editDesign.id}`, { name: editName });
      toast.success('Badge design updated!');
      setEditDesign(null);
      fetchAll();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleAwardClanBadge = async (clanId, designId) => {
    try {
      await axios.put(`${API}/admin/clans/${clanId}/badge`, { design_id: designId, action: 'add' });
      toast.success('Badge awarded to clan!');
      setClanAwardDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleRemoveClanBadge = async (clanId, designId) => {
    try {
      await axios.put(`${API}/admin/clans/${clanId}/badge`, { design_id: designId, action: 'remove' });
      toast.success('Badge removed from clan');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const goldDesigns = designs.filter(d => d.tier === 'gold');
  const silverDesigns = designs.filter(d => d.tier === 'silver');
  const bronzeDesigns = designs.filter(d => d.tier === 'bronze');

  if (authLoading || loading) {
    return (
      <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 px-6 bg-[#050505]" data-testid="admin-badges-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Award className="w-8 h-8 text-yellow-500" />
            <h1 className="font-heading text-3xl font-bold uppercase text-white">
              Badge Management
            </h1>
          </div>
          <p className="text-zinc-500">Create badge designs and award them to players</p>
        </div>

        {/* Badge Design Library */}
        {['gold', 'silver', 'bronze', 'mvp'].map(tier => {
          const style = TIER_STYLES[tier];
          const tierDesigns = designs.filter(d => d.tier === tier);
          return (
            <div key={tier} className="bg-[#121212] border border-zinc-800 p-6 mb-6" data-testid={`${tier}-designs-section`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`font-heading text-sm font-bold uppercase tracking-widest ${style.color}`}>
                  {style.label}
                </h2>
                <button
                  onClick={() => { setUploadTier(tier); setUploadName(''); setUploadFile(null); }}
                  className={`flex items-center gap-1 text-xs font-bold uppercase tracking-wide px-3 py-1.5 border ${style.border} ${style.color} ${style.hoverBg} transition-colors`}
                  data-testid={`add-${tier}-design-btn`}
                >
                  <Plus className="w-3 h-3" /> Add Design
                </button>
              </div>
              {tierDesigns.length === 0 ? (
                <p className="text-zinc-700 text-sm mb-4">No {tier} badge designs yet.</p>
              ) : (
                <div className="flex flex-wrap gap-3 mb-4">
                  {tierDesigns.map(d => (
                    <div key={d.id} className={`relative group border ${style.border} ${style.bg} p-3 flex flex-col items-center w-28`} data-testid={`design-${d.id}`}>
                      <img src={`${BASE}${d.image_url}`} alt={d.name} className="w-14 h-14 object-contain mb-2" />
                      <span className="text-[10px] text-zinc-400 text-center truncate w-full">{d.name}</span>
                      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => { setEditDesign(d); setEditName(d.name); }}
                          className="w-5 h-5 bg-cyan-500/80 text-white flex items-center justify-center"
                          data-testid={`edit-design-${d.id}`}
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteDesign(d.id, d.name)}
                          className="w-5 h-5 bg-red-500/80 text-white flex items-center justify-center"
                          data-testid={`delete-design-${d.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Premade badges button */}
              {premades.filter(p => p.tier === tier).length > 0 && (
                <div className="border-t border-zinc-800 pt-3 mt-2">
                  <button
                    onClick={() => setPremadeDialog(tier)}
                    className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-cyan-400 transition-colors flex items-center gap-1"
                    data-testid={`browse-premade-${tier}`}
                  >
                    <Plus className="w-3 h-3" /> Browse {premades.filter(p => p.tier === tier).length} Premade Designs
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Upload Dialog */}
        <Dialog open={!!uploadTier} onOpenChange={() => setUploadTier(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="upload-design-dialog">
            <DialogHeader>
              <DialogTitle className={`font-heading text-xl uppercase ${uploadTier ? TIER_STYLES[uploadTier].color : 'text-white'}`}>
                New {uploadTier ? TIER_STYLES[uploadTier].label : ''} Badge
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUploadDesign} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">Badge Name</Label>
                <Input
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  placeholder="e.g., Season 1 Champion"
                  required
                  className="bg-black border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 focus:border-cyan-400"
                  data-testid="design-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-sm uppercase tracking-wide">Badge Image</Label>
                <label className="block border border-dashed border-zinc-700 p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="design-file-input"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                  {uploadFile ? (
                    <span className="text-cyan-400 text-sm">{uploadFile.name}</span>
                  ) : (
                    <span className="text-zinc-600 text-sm flex flex-col items-center gap-2">
                      <Upload className="w-6 h-6" />
                      Click to select image
                    </span>
                  )}
                </label>
              </div>
              <Button
                type="submit"
                disabled={uploading || !uploadFile || !uploadName}
                className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12"
                data-testid="upload-design-submit"
              >
                {uploading ? 'Uploading...' : 'Create Badge Design'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Player Search */}
        <div className="relative mb-6 mt-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search players by name or email..."
            className="bg-[#121212] border-zinc-800 text-white placeholder:text-zinc-600 rounded-none h-12 pl-11 focus:border-cyan-400"
            data-testid="badge-search-input"
          />
        </div>

        {/* Players List */}
        <div className="space-y-2">
          {filtered.map(user => {
            const userBadges = user.badges || [];
            return (
              <div key={user.id} className="bg-[#121212] border border-zinc-800 p-4 flex items-center gap-4" data-testid={`badge-row-${user.id}`}>
                {/* Player info */}
                <div className="w-8 h-8 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm text-cyan-400 font-bold">{user.name?.charAt(0).toUpperCase() || '?'}</span>
                </div>
                <div className="w-40 flex-shrink-0">
                  <span className="text-white font-medium text-sm">{user.name}</span>
                  <p className="text-zinc-600 text-xs truncate">{user.email}</p>
                </div>

                {/* Current Badges */}
                <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                  {userBadges.length === 0 ? (
                    <span className="text-zinc-700 text-xs">No badges</span>
                  ) : (
                    userBadges.map((b, idx) => (
                      <div key={idx} className={`relative group flex items-center gap-1 px-2 py-1 border ${TIER_STYLES[b.tier]?.border || 'border-zinc-800'} ${TIER_STYLES[b.tier]?.bg || ''}`}>
                        <img src={`${BASE}${b.image_url}`} alt={b.name} className="w-5 h-5 object-contain" />
                        <span className="text-[10px] text-zinc-400 max-w-16 truncate">{b.name}</span>
                        <button
                          onClick={() => handleRemoveBadge(user.id, b.design_id)}
                          className="w-4 h-4 flex items-center justify-center text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remove this badge"
                          data-testid={`remove-badge-${user.id}-${idx}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {/* Award buttons - always open popup */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {['gold', 'silver', 'bronze', 'mvp'].map(tier => {
                    const tierDesigns = designs.filter(d => d.tier === tier);
                    const s = TIER_STYLES[tier];
                    return (
                      <button
                        key={tier}
                        onClick={() => {
                          if (tierDesigns.length === 0) {
                            toast.error(`No ${tier} badge designs. Create one first.`);
                            return;
                          }
                          setAwardDialog({ userId: user.id, userName: user.name, tier });
                        }}
                        className={`h-8 px-2.5 flex items-center gap-1 border ${s.border} ${s.bg} ${s.color} ${s.hoverBg} transition-colors text-xs font-bold`}
                        title={`Award ${s.label}`}
                        data-testid={`award-${tier}-${user.id}`}
                      >
                        <Plus className="w-3 h-3" />
                        {tier === 'gold' ? '1st' : tier === 'silver' ? '2nd' : tier === 'bronze' ? '3rd' : 'MVP'}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Clan Badges Section */}
        <div className="mt-12 mb-8">
          <h2 className="font-heading text-lg font-bold uppercase text-white mb-4">Clan Badges</h2>
          <div className="space-y-2">
            {clans.map(clan => {
              const clanBadges = clan.badges || [];
              return (
                <div key={clan.id} className="bg-[#121212] border border-zinc-800 p-4 flex items-center gap-4" data-testid={`clan-badge-row-${clan.id}`}>
                  <div className="w-8 h-8 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-cyan-400 font-bold font-mono">{clan.tag}</span>
                  </div>
                  <div className="w-40 flex-shrink-0">
                    <span className="text-white font-medium text-sm">{clan.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                    {clanBadges.length === 0 ? (
                      <span className="text-zinc-700 text-xs">No badges</span>
                    ) : (
                      clanBadges.map((b, idx) => (
                        <div key={idx} className={`relative group flex items-center gap-1 px-2 py-1 border ${TIER_STYLES[b.tier]?.border || 'border-zinc-800'} ${TIER_STYLES[b.tier]?.bg || ''}`}>
                          <img src={`${BASE}${b.image_url}`} alt={b.name || b.tier} className="w-5 h-5 object-contain" />
                          <span className="text-[10px] text-zinc-400 max-w-16 truncate">{b.name}</span>
                          <button onClick={() => handleRemoveClanBadge(clan.id, b.design_id)} className="w-4 h-4 flex items-center justify-center text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {['gold', 'silver', 'bronze', 'mvp'].map(tier => {
                      const tierDesigns = designs.filter(d => d.tier === tier);
                      const s = TIER_STYLES[tier];
                      return (
                        <button
                          key={tier}
                          onClick={() => {
                            if (tierDesigns.length === 0) { toast.error(`No ${tier} designs.`); return; }
                            setClanAwardDialog({ clanId: clan.id, clanName: clan.name, tier });
                          }}
                          className={`h-8 px-2.5 flex items-center gap-1 border ${s.border} ${s.bg} ${s.color} ${s.hoverBg} transition-colors text-xs font-bold`}
                          data-testid={`clan-award-${tier}-${clan.id}`}
                        >
                          <Plus className="w-3 h-3" />
                          {tier === 'gold' ? '1st' : tier === 'silver' ? '2nd' : tier === 'bronze' ? '3rd' : 'MVP'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Award Badge Picker Dialog */}
        <Dialog open={!!awardDialog} onOpenChange={() => setAwardDialog(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="award-badge-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                Award {awardDialog?.tier ? TIER_STYLES[awardDialog.tier].label : ''} to {awardDialog?.userName}
              </DialogTitle>
            </DialogHeader>
            <p className="text-zinc-500 text-sm">Select which badge to award:</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {awardDialog && designs.filter(d => d.tier === awardDialog.tier).map(d => (
                <button
                  key={d.id}
                  onClick={() => handleAwardBadge(awardDialog.userId, d.id)}
                  className={`border ${TIER_STYLES[awardDialog.tier].border} ${TIER_STYLES[awardDialog.tier].bg} ${TIER_STYLES[awardDialog.tier].hoverBg} p-4 flex flex-col items-center gap-2 transition-colors`}
                  data-testid={`pick-design-${d.id}`}
                >
                  <img src={`${BASE}${d.image_url}`} alt={d.name} className="w-12 h-12 object-contain" />
                  <span className="text-xs text-zinc-300 text-center">{d.name}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        {/* Premade Badge Picker Dialog */}
        <Dialog open={!!premadeDialog} onOpenChange={() => setPremadeDialog(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="premade-badge-dialog">
            <DialogHeader>
              <DialogTitle className={`font-heading text-xl uppercase ${premadeDialog ? TIER_STYLES[premadeDialog].color : 'text-white'}`}>
                Premade {premadeDialog ? TIER_STYLES[premadeDialog].label : ''} Badges
              </DialogTitle>
            </DialogHeader>
            <p className="text-zinc-500 text-sm mb-4">Click a badge to add it to your designs</p>
            <div className="grid grid-cols-4 gap-3">
              {premadeDialog && premades.filter(p => p.tier === premadeDialog).map(p => (
                <button
                  key={p.name}
                  onClick={() => { handleUsePremade(p.name, premadeDialog); }}
                  className={`border border-dashed ${TIER_STYLES[premadeDialog].border} ${TIER_STYLES[premadeDialog].bg} ${TIER_STYLES[premadeDialog].hoverBg} p-3 flex flex-col items-center gap-2 transition-all hover:border-solid`}
                  data-testid={`premade-${p.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <img src={`${BASE}${p.image_url}`} alt={p.name} className="w-14 h-14 object-contain" />
                  <span className="text-[10px] text-zinc-400 text-center">{p.name}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Clan Award Badge Picker Dialog */}
        <Dialog open={!!clanAwardDialog} onOpenChange={() => setClanAwardDialog(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-md" data-testid="clan-award-badge-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">
                Award {clanAwardDialog?.tier ? TIER_STYLES[clanAwardDialog.tier].label : ''} to {clanAwardDialog?.clanName}
              </DialogTitle>
            </DialogHeader>
            <p className="text-zinc-500 text-sm">Select which badge to award:</p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {clanAwardDialog && designs.filter(d => d.tier === clanAwardDialog.tier).map(d => (
                <button
                  key={d.id}
                  onClick={() => handleAwardClanBadge(clanAwardDialog.clanId, d.id)}
                  className={`border ${TIER_STYLES[clanAwardDialog.tier].border} ${TIER_STYLES[clanAwardDialog.tier].bg} ${TIER_STYLES[clanAwardDialog.tier].hoverBg} p-4 flex flex-col items-center gap-2 transition-colors`}
                  data-testid={`clan-pick-design-${d.id}`}
                >
                  <img src={`${BASE}${d.image_url}`} alt={d.name} className="w-12 h-12 object-contain" />
                  <span className="text-xs text-zinc-300 text-center">{d.name}</span>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Design Dialog */}
        <Dialog open={!!editDesign} onOpenChange={() => setEditDesign(null)}>
          <DialogContent className="bg-[#121212] border-zinc-800 rounded-none max-w-sm" data-testid="edit-design-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase">Edit Badge Design</DialogTitle>
            </DialogHeader>
            {editDesign && (
              <form onSubmit={handleEditDesign} className="space-y-4 mt-4">
                <div className="flex justify-center">
                  <img src={`${BASE}${editDesign.image_url}`} alt={editDesign.name} className="w-20 h-20 object-contain" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm uppercase tracking-wide">Badge Name</Label>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    required
                    className="bg-black border-zinc-800 text-white rounded-none h-12 focus:border-cyan-400"
                    data-testid="edit-design-name"
                  />
                </div>
                <Button type="submit" className="w-full bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none h-12" data-testid="save-design-edit">
                  Save Changes
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </main>
  );
}
